import { isDate } from "@omnia/shared/utils/utils"
import { CropYear, getCroppingDatesInYear, getFieldCroppingArea, ICrop, ICropping, isArrayEmpty, selectMany, sortDates, sum } from "../../omnia"
import { CroppingHistory, getHistory } from "./history"


export function setCropDefaultDates(target: ICropping, crop: ICrop, useDestructionDate: boolean) {

    const year = target.YearId

    if (crop.SowingDateReqd && isDate(crop.SowingDate)) {
        target.SowingDate = getDate(crop.SowingDate, year, true)
    }

    if (!useDestructionDate) {
        target.HarvestDate = null
        if (crop.HarvestDateReqd && isDate(crop.HarvestDate)) {
            target.HarvestDate = getDate(crop.HarvestDate, year, false)
            target.DestructionDate = null
            target.Destroyed = false
        }
    }

    if (useDestructionDate) {
        target.DestructionDate = null
        if (crop.DestructionDateReqd && omnia.isDate(crop.DestructionDate)) {
            target.DestructionDate = getDate(crop.DestructionDate, year, false)
            target.HarvestDate = null
        }
    }


}

/**
 * Adjusts the start/end date of a cropping after its crops has been changed,
 * and its dates have been reset to the new crop's default dates
 * This works by looking at the dates of other crops on the same fields,
 * and trying to avoid intersections with those
 */
export function adjustDates(target: ICropping, cropYear: CropYear) {

    const defaultMinDays = 60
    const minCropDaysRatio = 0.75

    if (!target.Field) return

    /**
     * 200710 - Usersnap 1649
     * The initial implementation (below) does not cover the basic case where a new crop is added on the last year,
     * but its default dates intersect with the latest crop on the previous year
     * {@link adjustDatesFromPrevYear} should cover this case
     */
    if (adjustDatesFromPrevYear(target)) {
        return
    }

    const history = getHistory(target)
    if (isArrayEmpty(history)) return

    const cropDates = target.Crop ? getCroppingDatesInYear(target) : null

    const minDays = cropDates
        ? Math.floor((cropDates.end.valueOf() - cropDates.start.valueOf()) * minCropDaysRatio)
        : defaultMinDays

    const endDate = cropYear.EndDate
    const prevYear = cropYear.Year - 1

    const root = history.find(h => (h.cropping.YearId == prevYear) && (h.end <= endDate))
    if (!root) return


    if (!checkAllFieldAreaUsed(root, cropYear.YearId)) {
        return
    }

    function getValidInterval(from: CroppingHistory, end: Date) {
        const start = from.end.addDays(1)
        const days = end.valueOf() - start.valueOf()
        return (days >= minDays) ? { start, end, days } : null
    }

    function findIntervals(from: CroppingHistory) {

        let intervals = from.next.map(n => getValidInterval(from, n.start.addDays(-1)))
        intervals = intervals.concat(selectMany(from.next.filter(n => n.start <= endDate).map(findIntervals)))
        intervals = intervals.filter(i => !!i)

        return intervals

    }

    const intervals = findIntervals(root).sort((a, b) => b.days - a.days)

    const match = intervals[0] || getValidInterval(root, endDate)
    if (!match) return

    const start = match.start > target.SowingDate ? match.start : target.SowingDate

    let end = target.HarvestDate || target.DestructionDate
    end = match.end < end ? match.end : end

    if (start < end) {

        target.SowingDate = start

        if (target.HarvestDate) {
            target.HarvestDate = end
        }

        if (target.DestructionDate) {
            target.DestructionDate = end
        }

    }

}

/**
 * Adjusts the starting date of a cropping by simply looking at the latest crop from earlier years
 *
 * Returns true if the starting date has been changed
 */
function adjustDatesFromPrevYear(target: ICropping) {

    const field = target.Field
    const year = target.YearId;

    let other = field.Cropping.filter(c => c.CroppingId != target.CroppingId)

    if (other.some(c => c.YearId > year)) {
        return false
    }

    other = other.filter(c => c.YearId == (year - 1))

    if (other.length == 0) {
        return false
    }

    const latestPrev = other.sort((a, b) => sortDates(b.EndDate, a.EndDate))[0]

    const start = target.SowingDate
    const end = latestPrev.EndDate.addDays(1)

    if (end <= start) {
        return false
    }

    target.SowingDate = end

    return true

}


/**
 * This function checks if, during @year, there are groups of crops which cover all the area of the field
 */
function checkAllFieldAreaUsed(root: CroppingHistory, year: number) {

    const fieldArea = getFieldCroppingArea(root.cropping.Field)

    function checkRank(from: CroppingHistory) {
        if (from.cropping.YearId > year) return 0
        const a = sum(from.next.map(checkRank), a => a)
        const b = sum(from.next || [], i => i.cropping.Area)
        return a > b ? a : b
    }

    const fieldAreaUsed = checkRank(root)

    return (fieldAreaUsed >= fieldArea)

}

function getDate(date: Date, year: number, start: boolean) {
    return CropYear.getDateInYear(date, year, start)
}
